import { FIELD_LABELS } from "../labels";
import { cutsRows, describeRows, listAllRows } from "../rows";

import type { CensusSuggestion } from "@platform/api/bp-api";
import type { CensusField } from "../hooks/useCensusValidation";

/** One suggestion as the broker reads it. */
export interface SuggestionText {
  readonly title: string;
  readonly details: readonly string[];

  /**
   * The same lines as `details` with every row listed. Empty when `details`
   * already lists them all.
   */
  readonly allDetails: readonly string[];
}

/**
 * A detail line naming rows, short and in full. The full form is kept only
 * when the short one leaves rows out.
 */
const rowLines = <Group extends { readonly rows: readonly number[] }>(
  groups: readonly Group[],
  line: (group: Group, rowText: string) => string,
): Pick<SuggestionText, "details" | "allDetails"> => ({
  details: groups.map((group) => line(group, describeRows(group.rows))),
  allDetails: groups.some(({ rows }) => cutsRows(rows))
    ? groups.map((group) => line(group, listAllRows(group.rows)))
    : [],
});

const isCensusField = (field: string): field is CensusField =>
  Object.hasOwn(FIELD_LABELS, field);

const fieldLabel = (field = ""): string =>
  isCensusField(field) ? FIELD_LABELS[field] : field;

/** "Dental", "Dental and Vision", "Dental, Vision and Basic Life". */
const listInWords = (items: readonly string[]): string =>
  items.length <= 1
    ? items.join("")
    : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

const quoted = (headings: readonly string[]): string =>
  listInWords(headings.map((heading) => `"${heading}"`));

const plural = (count: number, noun: string): string =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

const sentenceCase = (text: string): string =>
  text.charAt(0).toUpperCase() + text.slice(1);

const addColumn = ({
  field,
  lines = [],
  headers = [],
}: CensusSuggestion): SuggestionText => ({
  title: `Add a column for ${fieldLabel(field)}`,
  details: [
    `Needed for ${listInWords(lines)}.`,
    `We read it from a heading named ${quoted(headers)}.`,
  ],
  allDetails: [],
});

const fillColumn = ({
  field,
  headers = [],
  rows = [],
}: CensusSuggestion): SuggestionText => ({
  title: `Fill in ${fieldLabel(field)} for ${plural(rows.length, "employee")}`,
  ...rowLines(
    [{ rows }],
    (_, rowText) => `${quoted(headers)} is blank on ${rowText}.`,
  ),
});

const fixValues = ({
  column = "",
  values = [],
}: CensusSuggestion): SuggestionText => {
  const cells = values.reduce((total, group) => total + group.rows.length, 0);

  return {
    title: `Fix ${plural(cells, "cell")} we couldn't read in ${sentenceCase(column)}`,
    ...rowLines(values, ({ value }, rowText) =>
      value === undefined
        ? `${sentenceCase(rowText)}.`
        : `"${value}" on ${rowText}.`,
    ),
  };
};

type SuggestionKind = CensusSuggestion["kind"];

const describers: Readonly<
  Record<SuggestionKind, (suggestion: CensusSuggestion) => SuggestionText>
> = { addColumn, fillColumn, fixValues };

const UNDESCRIBED: SuggestionText = {
  title: "There's an issue in this file we can't describe yet",
  details: [],
  allDetails: [],
};

/**
 * A suggestion as it arrives. bp-api can add a kind before the wizard learns
 * it, so the kind is any string until it is checked.
 */
export type IncomingSuggestion = Omit<CensusSuggestion, "kind"> & {
  readonly kind: string;
};

const isKnown = (
  suggestion: IncomingSuggestion,
): suggestion is CensusSuggestion => Object.hasOwn(describers, suggestion.kind);

/**
 * What a suggestion says to the broker. An unknown kind still shows, as a line
 * without detail, rather than vanishing.
 */
export const describeSuggestion = (
  suggestion: IncomingSuggestion,
): SuggestionText =>
  isKnown(suggestion) ? describers[suggestion.kind](suggestion) : UNDESCRIBED;
